import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from "react";

/**
 * Reveal — one-shot IntersectionObserver fade+translateY reveal.
 * Restrained by design: 800ms, ease-out-quart, 16px offset, once only.
 * Respects prefers-reduced-motion (skips animation, content is always visible).
 */
export default function Reveal({
  children,
  as: Tag = "div",
  className,
  style,
  delay = 0,
  offset = 16,
  duration = 800,
  threshold = 0.18,
}: {
  children: ReactNode;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  style?: CSSProperties;
  delay?: number;
  offset?: number;
  duration?: number;
  threshold?: number;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const el = ref.current;
    if (!el) return;
    if (mq.matches) { setInView(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -5% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  const merged: CSSProperties = {
    ...style,
    opacity: reduced ? 1 : inView ? 1 : 0,
    transform: reduced ? "none" : inView ? "translate3d(0,0,0)" : `translate3d(0, ${offset}px, 0)`,
    transition: reduced
      ? "none"
      : `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
    willChange: reduced ? undefined : "transform, opacity",
  };

  return (
    // @ts-expect-error dynamic tag
    <Tag ref={ref} className={className} style={merged}>
      {children}
    </Tag>
  );
}